<template>
    <div class="leads-list">
        <div class="leads-list-header">
            <p class="leads-list-header-text">
                {{ $t("message.generics.message.totalRecords") }}: {{ count }}
            </p>
        </div>

        <v-data-table
            class="leads-list-table"
            :headers="headers"
            :items="list"
            :hide-actions="true"
            no-data-text=""
            no-results-text=""
            sort-icon="mdi-chevron-down"
            ref="table"
        >
            <v-progress-linear
                slot="progress"
                :color="providerColor"
                indeterminate
            />

            <template slot="items" slot-scope="props">
                <leads-table-item
                    :data-item="props.item"
                    @deleted="loadDataHandler"
                    :isExternal="isExternal"
                />
            </template>
        </v-data-table>

        <infinite-loading @infinite="load" :identifier="infiniteId">
            <div slot="no-results">
                <span class="no-results-text">Nenhum resultado encontrado</span>
            </div>
            <div slot="no-more"></div>
        </infinite-loading>
    </div>
</template>

<script>
import LeadsTableItem from "./LeadsTableItem";
import EventBus from "@packages/event-bus";
import InfiniteLoading from "vue-infinite-loading";
import provider from "@mixins/provider";

export default {
    components: {
        LeadsTableItem,
        InfiniteLoading,
    },

    props: {
        isExternal: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            entity: "leads",

            loadOnFilters: false,

            loading: false,

            infiniteId: +new Date(),

            page: 0,

            list: [],

            count: 0,

            hasMoreItems: true,
        };
    },

    beforeCreate() {
        this.$store.commit("LEADS_RESET_FILTER");
    },

    mounted() {
        EventBus.$on("LOAD_DATA", this.loadDataHandler);
    },

    beforeDestroy() {
        EventBus.$off("LOAD_DATA", this.loadDataHandler);
    },

    computed: {
        view() {
            return this.$store.state.leads.view;
        },

        filters() {
            return this.isExternal
                ? this.$store.state.leads.external.filters
                : this.$store.state.leads.filters;
        },

        amountPerPage() {
            if (!this.gridView) {
                return 30;
            }
            return this.$store.state.leads.colsPerPage * 8;
        },

        headers() {
            const columns = this.isAdminAndIsNotLoggedAs
                ? this.$store.state.leads.columnsToShowAdmin
                : this.$store.state.leads.columnsToShow;
            let customHeaders;
            if (!this.isExternal) {
                const columnsToShow = columns.filter((item) => item.showing);
                const formattedColumnsToShow = columnsToShow.map((item) => {
                    return {
                        text:
                            item.type === "Default"
                                ? this.translateName(item.label)
                                : item.label,
                        value: item.label,
                    };
                });
                customHeaders = formattedColumnsToShow;
            } else {
                customHeaders = [
                    {
                        text: this.$t("message.generics.message.name"),
                        value: "Nome",
                    },
                    {
                        text: this.$t("message.statistics.message.gender"),
                        value: "Sexo",
                    },
                    {
                        text: this.$t("message.statistics.message.birthDate"),
                        value: "dataNascimento",
                    },
                    {
                        text: this.$t("message.generics.message.email"),
                        value: "Email",
                    },
                    {
                        text: this.$t("message.generics.message.phoneNumber"),
                        value: "name",
                    },
                ];
            }
            return customHeaders;
        },

        isAdmin() {
            return this.$store.state.users.admin;
        },

        user() {
            return !!this.$store.state.users.user;
        },

        isAdminAndIsNotLoggedAs() {
            return this.isAdmin && !this.user;
        },
    },

    methods: {
        async load($state) {
            try {
                let data;
                if (!this.isExternal) {
                    const { content, totalElements } =
                        await this.$api.leads.list({
                            page: this.page,
                            filters: this.filters,
                        });
                    data = content;
                    this.count = totalElements;
                } else {
                    data = await this.$api.leads.listExternal({
                        page: this.page,
                        filters: this.filters,
                    });
                    this.count = await this.$api.leads.countExternal({
                        filters: this.filters,
                    });
                }

                if (data.length !== 0) {
                    this.list.push(...data);
                    this.page++;
                    $state.loaded();

                    if (this.list.length >= this.count) {
                        $state.complete();
                    }
                } else {
                    $state.complete();
                }
            } catch (error) {
                console.log(error);
                $state.complete();
            }
        },

        loadDataHandler() {
            this.list = [];
            this.page = 0;
            this.infiniteId++;
        },

        translateName(label) {
            let name;
            switch (label) {
                case "Nome":
                    name = this.$t("message.generics.message.name");
                    break;
                case "Sexo":
                    name = this.$t("message.statistics.message.gender");
                    break;
                case "Data de Nascimento":
                    name = this.$t("message.statistics.message.birthDate");
                    break;
                case "E-mail":
                    name = this.$t("message.generics.message.email");
                    break;
                case "Telefone":
                    name = this.$t("message.generics.message.phoneNumber");
                    break;
                default:
                    break;
            }
            return name;
        },
    },

    mixins: [provider],
};
</script>

<style lang="scss">
@import "~@variables";

.leads-list {
    th {
        pointer-events: none !important;

        color: rgba(0, 0, 0, 0.54) !important;
        &:hover {
            cursor: default !important;
        }
    }

    .mdi-chevron-down {
        display: none !important;
    }

    .leads-list-table {
        margin-bottom: 15px;
    }

    .leads-list-header {
        display: flex;
        justify-content: flex-end;
        margin-bottom: 10px;

        .leads-list-header-text {
            font-size: 14px;
            font-weight: 600;
            color: $grey-4;
            margin: 0;
        }
    }

    .grid {
        height: fit-content;
        display: flex;
        flex-wrap: wrap;
        margin: -14px;

        .loading {
            margin: 0 auto;
        }

        .no-data {
            text-align: center;
            width: 100%;
            font-weight: 400;
        }
    }

    .no-results-text {
        color: $grey-4;
    }
}
</style>
